/**
 * Unified Schedule Types
 *
 * TypeScript types for the unified schedule system that supports both
 * system tasks and workflow schedules via Temporal.
 *
 * 对应后端 api/schemas/schedule.py
 */

import type { RuntimeOptions, UserFilterOptions } from './imageWorkflow';
import type { WorkflowTargetType } from './users';

// ============================================================================
// Schedule Type Discriminator
// ============================================================================

/** 调度类型：系统任务 或 工作流 */
export type UnifiedScheduleType = 'system_task' | 'workflow';

// ============================================================================
// Config Types
// ============================================================================

/** 系统任务调度配置 (e.g., artifact_cleanup, transit_precompute) */
export interface SystemTaskScheduleConfig {
  task_type: string;
  config: Record<string, unknown>;
}

/** 工作流调度配置 */
export interface WorkflowScheduleConfig {
  workflow_id: string;
  target_type: WorkflowTargetType;
  target_user_ids?: string[] | null;
  user_filter?: UserFilterOptions | null;
  runtime_options?: RuntimeOptions | null;
}

// ============================================================================
// API Request Types
// ============================================================================

/** 创建统一调度请求 */
export interface CreateUnifiedScheduleRequest {
  name: string;
  description?: string | null;
  schedule_type: UnifiedScheduleType;
  cron_expression: string;
  timezone?: string; // 默认 Asia/Shanghai
  is_enabled?: boolean; // 默认 true
  system_task_config?: SystemTaskScheduleConfig | null;
  workflow_config?: WorkflowScheduleConfig | null;
}

/** 更新统一调度请求（所有字段可选） */
export interface UpdateUnifiedScheduleRequest {
  name?: string | null;
  description?: string | null;
  cron_expression?: string | null;
  timezone?: string | null;
  system_task_config?: SystemTaskScheduleConfig | null;
  workflow_config?: WorkflowScheduleConfig | null;
}

// ============================================================================
// API Response Types
// ============================================================================

/** 统一调度响应 */
export interface UnifiedScheduleResponse {
  id: string;
  name: string;
  description?: string | null;
  schedule_type: UnifiedScheduleType;
  cron_expression: string;
  timezone: string;
  is_enabled: boolean;
  temporal_schedule_id: string;
  system_task_config?: SystemTaskScheduleConfig | null;
  workflow_config?: WorkflowScheduleConfig | null;
  created_by: string;
  created_at: string;
  updated_at?: string | null;
}

/** 列表响应 */
export interface UnifiedScheduleListResponse {
  schedules: UnifiedScheduleResponse[];
}

/** 删除/触发等简单操作响应 */
export interface UnifiedScheduleActionResponse {
  message: string;
  schedule_id?: string;
}

/** 执行历史中的单次执行记录 */
export interface ScheduleRecentAction {
  scheduled_at: string | null;
  started_at: string | null;
}

/** 执行历史响应（来自 Temporal） */
export interface UnifiedScheduleExecutionsResponse {
  schedule_id: string;
  recent_actions: ScheduleRecentAction[];
  next_action_times: string[];
  error?: string;
}

// ============================================================================
// Query Parameters
// ============================================================================

export interface UnifiedScheduleListParams {
  schedule_type?: UnifiedScheduleType;
}

// ============================================================================
// Constants
// ============================================================================

export const UNIFIED_SCHEDULE_TYPE_LABELS: Record<UnifiedScheduleType, string> = {
  system_task: '系统任务',
  workflow: '工作流',
};
